// Right-slide drawer (Filter panel, Portal detail).
// README animation spec: 250ms cubic-bezier(0.16, 1, 0.3, 1) with backdrop blur.
// Shadow: -20px 0 40px rgba(20,20,18,0.08).

import { useEffect, type ReactNode } from 'react';

interface Props {
  open: boolean;
  onClose: () => void;
  width?: number;
  children: ReactNode;
}

export default function Drawer({ open, onClose, width = 440, children }: Props) {
  useEffect(() => {
    if (!open) return;
    const onEsc = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onEsc);
    return () => window.removeEventListener('keydown', onEsc);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <>
      <div
        onClick={onClose}
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(20,20,18,0.35)',
          backdropFilter: 'blur(2px)',
          zIndex: 40,
          animation: 'fadeUp 0.2s',
        }}
      />
      <div
        style={{
          position: 'fixed',
          top: 0,
          right: 0,
          bottom: 0,
          width,
          background: 'var(--surface)',
          borderLeft: '1px solid var(--line)',
          zIndex: 41,
          boxShadow: '-20px 0 40px rgba(20,20,18,0.08)',
          display: 'flex',
          flexDirection: 'column',
          animation: 'slideIn 0.25s cubic-bezier(0.16,1,0.3,1)',
        }}
      >
        {children}
      </div>
      <style>{`@keyframes slideIn { from { transform: translateX(20px); opacity: 0; } to { transform: none; opacity: 1; } }`}</style>
    </>
  );
}
